import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ArrowUpRight } from "lucide-react";

const nav = [
  { to: "/", label: "Accueil", hash: "" },
  { to: "/#cabinet", label: "Le Cabinet" },
  { to: "/services", label: "Nos Services" },
  { to: "/#honoraires", label: "Honoraires" },
  { to: "/recrutement", label: "Recrutement" },
  { to: "/contact", label: "Prendre RDV" },
];

const Header = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur border-ink-200" : "bg-white border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-5">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <div className="w-9 h-9 bg-teal-dark flex items-center justify-center">
            <span className="font-serif text-white text-xl leading-none">D</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif text-[17px] tracking-tight text-teal-dark">Ducrot &amp; Associés</div>
            <div className="eyebrow text-[10px] text-ink-500 -mt-0.5">Expertise comptable &amp; conseils</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <a
              key={n.label}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              href={n.to.startsWith("/#") ? n.to.replace("/", "") : n.to}
              onClick={(e) => {
                if (n.to.startsWith("/#") && loc.pathname === "/") {
                  e.preventDefault();
                  const id = n.to.replace("/#", "");
                  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
                } else if (n.to.startsWith("/#")) {
                  // fall back to Link-like navigation
                  window.location.href = n.to;
                }
              }}
              className="text-sm text-ink-800 hover:text-teal-accent transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user && user !== false ? (
            <>
              <Link
                to="/espace-client"
                data-testid="header-dashboard-link"
                className="text-sm text-teal-dark hover:text-teal-accent"
              >
                Espace client
              </Link>
              <button
                data-testid="header-logout-btn"
                onClick={logout}
                className="text-sm border border-teal-dark px-4 py-2 hover:bg-teal-l1 transition-colors"
              >
                Déconnexion
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-btn"
              className="text-sm border border-teal-dark px-4 py-2 hover:bg-teal-l1 transition-colors inline-flex items-center gap-2"
            >
              Espace client <ArrowUpRight size={14} />
            </Link>
          )}
          <Link to="/contact" data-testid="header-contact-cta" className="btn-primary text-sm">
            Nous contacter
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-teal-dark"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-ink-200 px-6 py-4 space-y-3" data-testid="mobile-menu">
          {nav.map((n) => (
            <a
              key={n.label}
              href={n.to}
              className="block text-ink-800 py-2"
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >{n.label}</a>
          ))}
          {user && user !== false ? (
            <>
              <Link to="/espace-client" className="block text-teal-dark py-2">Espace client</Link>
              <button onClick={logout} className="block text-teal-dark py-2">Déconnexion</button>
            </>
          ) : (
            <Link to="/login" className="block text-teal-dark py-2">Espace client</Link>
          )}
          <Link to="/contact" className="btn-primary text-sm w-full justify-center">Nous contacter</Link>
        </div>
      )}
    </header>
  );
};

export default Header;
